/**
 * Sentry beforeSendLog handler to filter out noise.
 * Filters static asset requests and info logs from unauthenticated marketing website visitors.
 */

import { isProtectedRoute } from '@/lib/routes';
import { isStaticAsset } from '@/lib/utils/static-assets';

/**
 * Sentry log entry type.
 * Compatible with Sentry's Log type but using a more flexible interface
 * since the exact Log type may not be exported from @sentry/nextjs.
 */
type SentryLog = {
  level?: string;
  url?: string;
  message?: string;
  user?: {
    id?: string;
    [key: string]: unknown;
  };
  attributes?: {
    url?: string;
    pathname?: string;
    userId?: string;
    user?: {
      id?: string;
      [key: string]: unknown;
    };
    [key: string]: unknown;
  };
  [key: string]: unknown;
};

/**
 * Extract URL from log entry, checking multiple possible locations.
 * Sentry logs may store URLs in different places depending on context.
 *
 * @param log - Sentry log entry
 * @returns URL string if found, undefined otherwise
 */
function getLogUrl(log: SentryLog): string | undefined {
  const url = log.url || log.attributes?.url || log.attributes?.pathname || log.message;
  return url && typeof url === 'string' ? url : undefined;
}

/**
 * Extract user ID from log entry, checking multiple possible locations.
 *
 * @param log - Sentry log entry
 * @returns User ID if found, null if explicitly null, undefined if not found
 */
function getLogUserId(log: SentryLog): string | null | undefined {
  return log.user?.id || log.attributes?.userId || log.attributes?.user?.id;
}

/**
 * Check if a log is for a static asset request that should be filtered out.
 * Uses the shared static asset detection utility.
 *
 * Checks multiple locations in the log object to catch static asset URLs:
 * - URL fields (url, attributes.url, attributes.pathname, message)
 * - All string values in the log object (recursively)
 * - Log message itself
 */
function isStaticAssetRequest(log: SentryLog): boolean {
  // First check the URL from common fields (fast path)
  const url = getLogUrl(log);
  if (url && isStaticAsset(url)) {
    return true;
  }

  // Check the log message itself
  if (log.message && typeof log.message === 'string' && isStaticAsset(log.message)) {
    return true;
  }

  // Check all string values in the log object recursively
  // This catches static asset URLs that might be in unexpected fields
  function checkValue(value: unknown): boolean {
    if (typeof value === 'string') {
      return isStaticAsset(value);
    }
    if (Array.isArray(value)) {
      return value.some(checkValue);
    }
    if (value && typeof value === 'object') {
      return Object.values(value).some(checkValue);
    }
    return false;
  }

  return checkValue(log);
}

/**
 * Check if we can confirm a log is from an unauthenticated user.
 * Returns true only if we can explicitly find that there's no user ID.
 * Returns false if user info is missing (to avoid over-filtering authenticated users
 * when user info isn't in the log object but is in Sentry scope).
 */
function canConfirmUnauthenticated(log: SentryLog): boolean {
  const userId = getLogUserId(log);
  // Only return true if user ID is explicitly null/undefined
  // If user info is completely missing from log object, return false (assume authenticated)
  return userId === null || (userId === undefined && 'user' in log);
}

/**
 * Check if a log is for a public/marketing route (not a protected dashboard route).
 */
function isPublicRoute(log: SentryLog): boolean {
  const url = getLogUrl(log);
  if (!url) {
    return false;
  }

  // If it's a protected route, it's not a public route
  return !isProtectedRoute(url);
}

/**
 * Create a beforeSendLog handler that filters out noise.
 * Only filters:
 * 1. Static asset requests (always filtered)
 * 2. Info logs from root path "/" requests from unauthenticated users (just marketing visits)
 * 3. Info logs from unauthenticated users on public routes (marketing website visitors)
 *
 * All errors, warnings, and authenticated user logs are kept.
 *
 * @returns A function that filters Sentry logs
 *
 * @example
 * ```typescript
 * const handler = createBeforeSendLog();
 *
 * // Static asset - filtered
 * handler({ level: 'info', url: '/_next/static/chunk.js' }); // null
 *
 * // Unauthenticated user on public route - filtered
 * handler({ level: 'info', url: '/', user: null }); // null (user explicitly null)
 * handler({ level: 'info', url: '/', user: { id: null } }); // null (user.id explicitly null) (user explicitly null)
 * handler({ level: 'info', url: '/', user: { id: null } }); // null (user.id explicitly null)
 *
 * // Error from unauthenticated user - kept
 * handler({ level: 'error', url: '/', user: null }); // log object
 *
 * // Authenticated user - kept
 * handler({ level: 'info', url: '/', user: { id: 'user-123' } }); // log object
 * ```
 */
export function createBeforeSendLog() {
  // Return type must match Sentry's expected signature: (log: Log) => Log | null.
  // Sentry's Log type isn't reliably exported, so the public signature uses `any`
  // for assignability to Sentry's option type, while we narrow to SentryLog internally.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (logInput: any): any => {
    const log = logInput as SentryLog;
    // Always filter out static asset requests (noise)
    if (isStaticAssetRequest(log)) {
      return null;
    }

    // Keep all errors and warnings - only filter info logs
    if (log.level !== 'info') {
      return log;
    }

    // For info logs, only filter if we can confirm BOTH:
    // 1. It's a public route (has URL and it's public, not protected)
    // 2. User is confirmed unauthenticated (user info explicitly missing from log)
    //
    // Default to keeping logs if we can't determine route or user status
    // (to avoid filtering authenticated users when user info is only in Sentry scope)

    const url = getLogUrl(log);
    if (!url) {
      // No URL - keep the log (can't determine if it's public)
      return log;
    }

    if (!isPublicRoute(log)) {
      // Protected route - keep the log
      return log;
    }

    if (!canConfirmUnauthenticated(log)) {
      // Can't confirm unauthenticated - keep the log (assume authenticated)
      return log;
    }

    // Confirmed: info log, public route, unauthenticated user - filter it
    return null;
  };
}
